import { Link } from 'react-router-dom';
import { usePageMeta } from '../lib/meta';

const EFFECTIVE_DATE = 'July 18, 2026';

export function CookiesPage() {
  usePageMeta({
    title: 'Cookie Policy',
    description:
      'The cookies and similar technologies used on PeacockOmlettes.com, who sets them, and how to control them.',
    path: '/cookies',
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <h1 className="font-display text-3xl text-plume-950 sm:text-5xl">Cookie Policy</h1>
      <p className="mt-3 text-sm text-plume-600">Effective date: {EFFECTIVE_DATE}</p>

      <div className="mt-8 space-y-8 leading-relaxed text-plume-900">
        <section>
          <h2 className="font-display text-2xl text-plume-950">The short version</h2>
          <p className="mt-3">
            We set no cookies of our own. The only cookies or similar technologies on this site come
            from Google, in two roles: remembering your consent choices, and (only where permitted
            by your choices and your region&rsquo;s law) serving and measuring ads.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-plume-950">Cookies by category</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse text-left text-sm">
              <caption className="sr-only">
                Categories of cookies used on this site, their provider, and purpose
              </caption>
              <thead>
                <tr className="border-b-2 border-plume-200">
                  <th scope="col" className="py-3 pr-4 font-semibold text-plume-950">
                    Category
                  </th>
                  <th scope="col" className="py-3 pr-4 font-semibold text-plume-950">
                    Provider
                  </th>
                  <th scope="col" className="py-3 font-semibold text-plume-950">
                    Purpose
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-plume-100">
                <tr>
                  <th scope="row" className="py-3 pr-4 align-top font-semibold">
                    Strictly necessary
                  </th>
                  <td className="py-3 pr-4 align-top">Google (consent framework)</td>
                  <td className="py-3 align-top">
                    Stores your consent choices (for example the IAB TCF consent string) so we do
                    not have to ask on every visit. Exempt from consent because it exists to record
                    consent.
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="py-3 pr-4 align-top font-semibold">
                    Advertising
                  </th>
                  <td className="py-3 pr-4 align-top">Google AdSense and certified partners</td>
                  <td className="py-3 align-top">
                    Ad delivery, frequency capping, fraud prevention, measurement, and (only with
                    consent where required) personalization. See Google&rsquo;s{' '}
                    <a
                      href="https://policies.google.com/technologies/cookies"
                      rel="noopener noreferrer"
                      className="font-semibold underline underline-offset-4"
                    >
                      cookie documentation
                    </a>{' '}
                    for the specific cookies in current use.
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="py-3 pr-4 align-top font-semibold">
                    Analytics
                  </th>
                  <td className="py-3 pr-4 align-top">None</td>
                  <td className="py-3 align-top">
                    We run no analytics product. Ad reporting comes from AdSense&rsquo;s own
                    aggregate dashboard.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl text-plume-950">Your controls</h2>
          <ul className="mt-3 list-disc space-y-2 pl-6">
            <li>
              Review or change your consent choices any time on the{' '}
              <Link to="/privacy-settings" className="font-semibold underline underline-offset-4">
                Privacy Settings
              </Link>{' '}
              page.
            </li>
            <li>
              Opt out of Google ad personalization at{' '}
              <a
                href="https://adssettings.google.com"
                rel="noopener noreferrer"
                className="font-semibold underline underline-offset-4"
              >
                adssettings.google.com
              </a>
              , or industry-wide at{' '}
              <a
                href="https://optout.aboutads.info"
                rel="noopener noreferrer"
                className="font-semibold underline underline-offset-4"
              >
                optout.aboutads.info
              </a>
              .
            </li>
            <li>
              Enable the <strong>Global Privacy Control</strong> signal in your browser; we treat
              it as a binding opt-out everywhere.
            </li>
            <li>
              Block or delete cookies in your browser settings. The site remains fully usable with
              all cookies blocked; only the ads are affected.
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}
